import type {
  BuyerInfo,
  CourseBookingSelection,
  PriceQuantities,
} from "@/lib/courseBookingContext";

// コース詳細〜購入者情報入力で入力した予約内容の下書き (JPTP_CANSEI-1428)。
// Stripe Checkout へは window.location.href のフルページ遷移で出るため、cancel_url で
// 戻ると SPA が再起動し CourseBookingProvider の state が初期値に戻ってしまう。
// sessionStorage は同一タブの Stripe 往復で保持されるため、そこへ退避して復帰後に復元する
// (lastShopLocation と同じ仕組み)。
const STORAGE_KEY = "viajero.guest.bookingDraft";

// 下書きの復元先を限定するコース詳細配下のルートパラメータ。別の案件・店舗・商品を開いた
// ときに前の入力値が復元されないよう、保存時と一致した場合だけ復元する。
export type BookingDraftScope = {
  caseToken: string;
  shopId: string;
  productId: string;
};

// 保存する購入者情報。emailVerified は含めない。CreateCheckoutSession が Checkout Session の
// 発行時に認証を消し込む (consumed_at) ため、画面だけ認証済みに戻してもサーバ側では無効で、
// 復帰後はメールアドレス認証からやり直してもらう必要がある (ゲスト予約・決済_詳細設計 §6.4)。
export type BookingDraftBuyerInfo = Omit<BuyerInfo, "emailVerified">;

export type BookingDraft = BookingDraftScope & {
  selection: CourseBookingSelection;
  buyerInfo: BookingDraftBuyerInfo;
  // 決済に進んだ予約の ID も保存して再決済で同じ予約を使い回す。作り直すと旧 Stripe セッションが
  // 有効なまま新セッションが並存するが、同じ予約なら CreateCheckoutSession が旧セッションを
  // expire してから再発行するため二重決済を防げる (§7.2)。
  reservationId: string | null;
  // Stripe Checkout へ遷移済みか。決済せずに戻ってきたときの案内表示の判定に使う。
  checkoutStarted: boolean;
};

// 価格種別 ID ごとの人数を検証する。人数は 0 以上の整数のみ許可する。
function parseQuantities(value: unknown): PriceQuantities | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const quantities: PriceQuantities = {};
  for (const [priceTypeId, quantity] of Object.entries(value)) {
    if (typeof quantity !== "number" || !Number.isInteger(quantity) || quantity < 0) return null;
    quantities[priceTypeId] = quantity;
  }
  return quantities;
}

// 予約内容 (利用日時・人数・同意) を検証する。日時・人数は未入力のまま保存され得るため、
// 空文字・空オブジェクトは正当な値として通す (スコープと違い入力途中でも復元したい)。
function parseSelection(value: unknown): CourseBookingSelection | null {
  if (typeof value !== "object" || value === null) return null;
  const { date, time, quantities, agreedTerms, agreedPrivacy, agreedCancellation } =
    value as Partial<CourseBookingSelection>;
  if (typeof date !== "string" || typeof time !== "string") return null;
  if (
    typeof agreedTerms !== "boolean" ||
    typeof agreedPrivacy !== "boolean" ||
    typeof agreedCancellation !== "boolean"
  ) {
    return null;
  }
  const parsedQuantities = parseQuantities(quantities);
  if (!parsedQuantities) return null;
  return {
    date,
    time,
    quantities: parsedQuantities,
    agreedTerms,
    agreedPrivacy,
    agreedCancellation,
  };
}

// 購入者情報を検証する。各項目は入力途中で空文字のまま保存され得るため空文字も通す。
function parseBuyerInfo(value: unknown): BookingDraftBuyerInfo | null {
  if (typeof value !== "object" || value === null) return null;
  const { lastName, firstName, phoneNumber, email, allergy, request } =
    value as Partial<BookingDraftBuyerInfo>;
  if (
    typeof lastName !== "string" ||
    typeof firstName !== "string" ||
    typeof phoneNumber !== "string" ||
    typeof email !== "string" ||
    typeof allergy !== "string" ||
    typeof request !== "string"
  ) {
    return null;
  }
  return { lastName, firstName, phoneNumber, email, allergy, request };
}

/**
 * sessionStorage から読んだ生文字列を BookingDraft として検証する純関数。
 * 未保存 (null)・壊れた JSON・型不一致はすべて null を返す。
 * Web Storage を触らないため bun test で単体テストできる (lastShopLocation と同方式)。
 */
export function parseBookingDraft(raw: string | null): BookingDraft | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const { caseToken, shopId, productId, selection, buyerInfo, reservationId, checkoutStarted } =
    parsed as Partial<BookingDraft>;
  // スコープは復元先の照合に使うため、空文字は照合できず不正として扱う。
  if (typeof caseToken !== "string" || caseToken === "") return null;
  if (typeof shopId !== "string" || shopId === "") return null;
  if (typeof productId !== "string" || productId === "") return null;
  // 予約未作成 (決済に進む前) は null。空文字は予約 ID として使えないため不正として扱う。
  if (reservationId !== null && (typeof reservationId !== "string" || reservationId === "")) {
    return null;
  }
  if (typeof checkoutStarted !== "boolean") return null;
  const parsedSelection = parseSelection(selection);
  if (!parsedSelection) return null;
  const parsedBuyerInfo = parseBuyerInfo(buyerInfo);
  if (!parsedBuyerInfo) return null;
  return {
    caseToken,
    shopId,
    productId,
    selection: parsedSelection,
    buyerInfo: parsedBuyerInfo,
    reservationId,
    checkoutStarted,
  };
}

/**
 * 下書きを保存する。入力のたびに呼ばれる。
 * プライベートモード・容量超過で setItem が throw しても予約フローを止めないよう握り潰す
 * (退避の失敗は「復帰後に入力値が戻らない」に留める)。
 *
 * スコープが欠けた下書きは parseBookingDraft でも弾かれるため、保存側でも捨てる
 * (片方だけ緩いと「保存したのに復元されない」という無言の不整合になる)。
 */
export function saveBookingDraft(draft: BookingDraft): void {
  if (!draft.caseToken || !draft.shopId || !draft.productId) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // 退避できなくても予約フローは継続する。
  }
}

/**
 * sessionStorage から読んだ生文字列を下書きとして解釈し、スコープが一致する場合だけ返す純関数。
 * 未保存・不正な値・スコープ違いはすべて null。
 */
function parseScopedBookingDraft(
  raw: string | null,
  scope: BookingDraftScope,
): BookingDraft | null {
  const draft = parseBookingDraft(raw);
  if (!draft) return null;
  if (
    draft.caseToken !== scope.caseToken ||
    draft.shopId !== scope.shopId ||
    draft.productId !== scope.productId
  ) {
    return null;
  }
  return draft;
}

/** 保存した下書きを読み出す。未保存・不正な値・スコープ違いはすべて null。 */
export function loadBookingDraft(scope: BookingDraftScope): BookingDraft | null {
  try {
    return parseScopedBookingDraft(window.sessionStorage.getItem(STORAGE_KEY), scope);
  } catch {
    return null;
  }
}

/**
 * 決済へ出た印と決済に進んだ予約の ID を立てた下書きを返す純関数 (markCheckoutStarted の本体)。
 * 下書きが無い・スコープ違いの場合は null (更新しない)。
 * Web Storage を触らないため bun test で単体テストできる (parseBookingDraft と同方式)。
 */
export function withCheckoutStarted(
  raw: string | null,
  scope: BookingDraftScope,
  reservationId: string,
): BookingDraft | null {
  const draft = parseScopedBookingDraft(raw, scope);
  if (!draft) return null;
  return { ...draft, reservationId, checkoutStarted: true };
}

/**
 * Stripe Checkout へリダイレクトする直前に、決済へ出た印と決済に進んだ予約の ID を残す。
 * 印は復帰時に「決済が完了していません」の案内を出す判定に、予約 ID は復帰後の再決済で同じ予約を
 * 指定する (旧セッションを expire させる。§7.2) のに使う。下書きが無ければ何もしない。
 * 予約 ID は CreateCheckoutSession の応答で初めて得られ、直後にフルページ遷移するため、
 * state 経由の退避 (useEffect) を待たずにここで同期的に書き込む (JPTP_CANSEI-1678)。
 */
export function markCheckoutStarted(scope: BookingDraftScope, reservationId: string): void {
  let draft: BookingDraft | null;
  try {
    draft = withCheckoutStarted(window.sessionStorage.getItem(STORAGE_KEY), scope, reservationId);
  } catch {
    return;
  }
  if (!draft) return;
  saveBookingDraft(draft);
}

/**
 * sessionStorage から読んだ生文字列に決済へ出た印が残っているかを判定する純関数。
 * 未保存・壊れた JSON・印が立っていない下書きはすべて false。
 * Web Storage を触らないため bun test で単体テストできる (parseBookingDraft と同方式)。
 */
export function isCheckoutStarted(raw: string | null): boolean {
  return parseBookingDraft(raw)?.checkoutStarted === true;
}

/**
 * 決済へ出た印が残っているかを返す。決済完了画面 (success_url) で「Stripe から戻ってきた
 * 直後の 1 回」を判定し、GA イベントを送るために使う (JPTP_CANSEI-1656)。
 * 決済完了画面はスコープ (caseToken / shopId / productId) を持たないため、
 * loadBookingDraft と違いスコープの照合は行わない。
 * 下書きは決済完了画面のマウント時に捨てるため、リロードや別タブでの再訪では false になる。
 */
export function hasCheckoutStarted(): boolean {
  try {
    return isCheckoutStarted(window.sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return false;
  }
}

/**
 * 下書きを削除する。決済完了画面で呼ぶ。
 * 確定済み予約の reservationId が復元されると、コース画面へ戻ったときの「決済に進む」が
 * 必ず失敗する行き止まりになるため、完了時点で捨てる。
 */
export function clearBookingDraft(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // 削除できなくても次の保存で上書きされる。
  }
}
